import { captureException } from '@sentry/react';
import type { PostHog } from 'posthog-js';
import config from '~/config';

// Use "location?" for not failing unit tests
const enableAnalytics = config.environment !== 'dev';

/**
 * Lazy-load posthog client only when events are used to avoid having it part of every bundle.
 * That way it costs us nothing to have it available in the codebase.
 */
let _posthogPromise: Promise<PostHog> | null = null;
const lazyLoadClient = async () => {
  if (_posthogPromise) {
    return _posthogPromise;
  }
  // Allow max 500ms to wait for posthog loaded state since we might prevent rendering otherwise
  _posthogPromise = import('posthog-js')
    .then(({ posthog }) => {
      posthog.init('phc_kqixdPiJcf3OETOVmnip0rBeBZz6CAZya64q7uN2ZIq', {
        // API configured with reverse proxy YARP on this endpoint
        api_host: '/api/ph',
        ui_host: 'https://eu.posthog.com',
        persistence: 'memory',
        // disable all auto capture
        autocapture: false,
        capture_pageview: false,
        capture_performance: false,
        capture_heatmaps: false,
        capture_dead_clicks: false,
        capture_pageleave: false,
        enable_recording_console_log: false,
        advanced_disable_feature_flags: true,
        before_send: (payload) => {
          if (payload?.properties) {
            payload.properties.consent_given = getHasGivenConsent();
            // Nothing else tells prod, uat and dev apart in PostHog
            payload.properties.environment = config.environment;
          }
          return enableAnalytics ? payload : null;
        },
      });
      return posthog;
    })
    .catch((e) => {
      _posthogPromise = null; // allow retry on next call
      throw e; // re-throw so callers still see the rejection
    });

  return _posthogPromise;
};

// Used to assert analytics events in e2e tests
const posthogEventLog = (globalThis as any)?.posthogEventLog as undefined | Array<any>;

const _captureEvent: PostHog['capture'] = (event_name, properties, options) => {
  // Next line used in e2e tests only
  if (posthogEventLog) {
    posthogEventLog.push({ event: event_name, properties });
  }

  lazyLoadClient()
    .then((posthog) => {
      posthog.capture(event_name, properties, options);
    })
    .catch((e) => {
      captureException(new Error('Failed to load posthog script', { cause: e }), { fingerprint: ['posthog-load-failure'] });
    });
  return undefined;
};

/**
 * Every event is sent as it happens. There is deliberately no client-side
 * deduplication: filtering is reversible in PostHog (count unique users or sessions
 * instead of total events), dropping an event here is not - and suppressing repeats
 * would also freeze the properties of the first occurrence, hiding later basket changes.
 * Repeat visits to a step are signal in their own right.
 *
 * For events fired right before leaving the page, pass `{ send_instantly: true }` so they
 * are not sitting in the batch queue when the document goes away, and
 * `transport: 'sendBeacon'` so the browser still delivers the request during unload.
 */
export const captureUXEvent = (
  event: string,
  properties: Record<string, any> = {},
  options?: { send_instantly?: boolean; transport?: 'XHR' | 'fetch' | 'sendBeacon' },
) => {
  try {
    _captureEvent(event, properties, options);
  } catch {
    /* om nom nom */
  }
};

const _debounceTimers: Record<string, ReturnType<typeof setTimeout>> = {};

/**
 * Collapses repeated calls into one, keeping the properties of the last call. This is for
 * accidental double invocation - react effects re-running, rage clicks - not for
 * suppressing genuine repeat interactions.
 *
 * Debouncing is per `key`, which defaults to the event name. Pass an explicit key when the
 * same event legitimately fires several times in a row for different subjects, e.g. one
 * event per product when a whole bundle is removed - keying on the event name alone would
 * collapse those into a single event.
 *
 * Do not use for events fired immediately before navigating away; use captureUXEvent with
 * `send_instantly` instead.
 */
export const captureUXEventDebounced = (
  event: string,
  properties: Record<string, any> = {},
  { key = event, delayMs = 150 }: { key?: string; delayMs?: number } = {},
) => {
  // Start loading the client now rather than when the timer fires. Otherwise the debounce
  // also delays the import, which matters for a later event that has to leave before a
  // navigation - it can only be sent once the client has loaded.
  lazyLoadClient().catch(() => {
    /* reported by the capture path */
  });
  clearTimeout(_debounceTimers[key]);
  _debounceTimers[key] = setTimeout(() => captureUXEvent(event, properties), delayMs);
};

function getHasGivenConsent(): boolean {
  try {
    return (
      document.cookie
        .split('; ')
        .find((c) => c.startsWith('wscrCookieConsent'))
        ?.includes('3=true') ?? false
    );
  } catch {
    return false;
  }
}
